import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { BRAND } from '@/brand.config';

interface PostRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  meetingId: string;
  email: string;
}

export default function PostRegistrationModal({ open, onClose, meetingId, email }: PostRegistrationModalProps) {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    if (!open || !meetingId) return;
    supabase
      .from('audience_submissions')
      .select('id', { count: 'exact', head: true })
      .eq('meeting_id', meetingId)
      .then(({ count: c }) => {
        if (c && c > 0) setCount(c);
      });
  }, [open, meetingId]);

  // Per-device, per-meeting flag. Keyed by meetingId only (no email) to
  // avoid persisting raw emails in localStorage.
  const storageKey = `signup_modal_seen_${meetingId}`;

  const handleClose = () => {
    localStorage.setItem(storageKey, 'true');
    onClose();
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setIsSubmitting(true);
    try {
      await supabase.from('audience_submissions').insert({
        meeting_id: meetingId,
        subscriber_email: email,
        submission_type: 'question',
        content: content.trim(),
      });
    } catch (err) {
      console.error('[PostRegistrationModal] Submit error:', err);
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-[calc(100vw-3rem)] sm:max-w-sm rounded-2xl p-5 border-border/50">
        <DialogHeader>
          <DialogTitle className="font-serif text-lg">{BRAND.postSignupTitle}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {BRAND.postSignupQuestion}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?"
            className="min-h-[72px] resize-none rounded-xl bg-accent/30 border-border/30 text-foreground placeholder:text-muted-foreground"
            maxLength={500}
          />

          {count > 0 && (
            <p className="text-xs text-muted-foreground">
              {count} {count === 1 ? 'person has' : 'people have'} submitted so far
            </p>
          )}

          <div className="flex flex-col gap-2">
            <Button
              onClick={handleSubmit}
              disabled={!content.trim() || isSubmitting}
              className="w-full rounded-xl font-serif font-bold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send it'
              )}
            </Button>
            <button
              type="button"
              onClick={handleClose}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              Skip for now
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
